import React, { useState, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Modal
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system';
import Tesseract from 'tesseract.js';
import { API_BASE_URL } from '../config/api';
import Constants from "expo-constants";
// Note: Registration endpoint not documented in current API specification
// import { registerUser } from '../services/authApi';
import '../styles/custom.css';
import { useFonts } from 'expo-font';
import { HeroOutline } from '@nandorojo/heroicons';
import { useCustomAlertModal } from '../components/CustomAlertModal';
import { useBannerWithPopUp } from '../components/BannerWithPopUp';

const { width, height } = Dimensions.get('window');

export default function PasswordCreateScreen({ navigation }) {
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();
  const { 
    bannerWrapper
  } = useBannerWithPopUp( { marginTop: 16 } );

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    console.log('=== FORM VALIDATION START ===');

    if (!password.trim()) {
      console.log('VALIDATION FAILED: No password provided');
      showCustomAlert('Error', 'Password harus diisi');
      return false;
    }

    if (password.length < 8) {
      console.log('VALIDATION FAILED: Password too short:', password.length);
      showCustomAlert('Error', 'Password minimal 8 karakter');
      return false;
    }

    if (password !== confirmPassword) {
      console.log('VALIDATION FAILED: Password mismatch');
      showCustomAlert('Error', 'Konfirmasi password tidak sesuai');
      return false;
    }

    console.log('=== FORM VALIDATION PASSED ===');
    return true;
  };

  const handleCreatePassword = async () => {
    console.log('handleCreatePassword called');
    console.log('isLoading:', isLoading);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, starting registration...');
    setIsLoading(true);

    try {
      // Prepare registration data according to updated API specification
      const registrationData = {
        'telp': phone || "",
        'password': password || "",
        'password-confirm': confirmPassword || "",
      };

      console.log('Sending registration request...');

      const response = await fetch(`${API_BASE_URL}/api/phone-create-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      const result = await response.json();
      // console.log('Registration response:', result);

      if (result.status === 'success' && result.data) {
        // make sure the password saved
        if( !result.data.hasPassword ) {
          setTimeout(() => {
            window.location.reload()
          }, 2000);
        } else {
          // save password in localStorage
          // coz we need to simplify user to not type password everytime
          // this will be replaced only when user register new number and make new password
          await AsyncStorage.setItem('zclub-credential', JSON.stringify({
            'phone' : phone,
            'password' : btoa(password)
          }));

          // Store user data if registration successful
          if (result.data.userData) {
            await AsyncStorage.setItem('userData', JSON.stringify(result.data.userData));

            // Store session data if provided
            if (result.data.userData.remember_token) {
              await AsyncStorage.setItem('userToken', result.data.userData.remember_token);
            }
            // Store session data if provided
            if (result.data.userData.session_id) {
              await AsyncStorage.setItem('session_id', result.data.userData.session_id);
            }
          }

          window.location.reload();
        }
      } else {
        showCustomAlert('Error', result.message || 'Pendaftaran gagal. Silakan coba lagi.');
      }

    } catch (error) {
      console.error('Registration error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showCustomAlert('Error', 'Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
      } else {
        showCustomAlert('Error', error.message || 'Pendaftaran gagal. Silakan coba lagi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useFocusEffect(
    React.useCallback(()=>{
      async function fetchLocalData() {
        const userDataString = await AsyncStorage.getItem('userData');
        const userData = JSON.parse(userDataString);
        if( userData?.user_telp ) {
          setPhone(userData?.user_telp);
        } else {
          navigation.navigate('PhoneAuth');
        }
      }

      fetchLocalData();
    }, [navigation])
  );

  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  // Remove showAuthAlert function as we no longer need authentication restrictions

  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Loading...</Text>
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Ionicons name="arrow-back" size={24} color="#000000" />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <View style={styles.titleSection}>
              <View style={styles.titleIconContainer}>
                <HeroOutline.LockClosed height="40" width="40" color="#1E293B" />
              </View>
              <Text style={styles.title}>Masukkan Password</Text>
              <Text style={styles.subtitle}>Buat password akun Anda.</Text>
            </View>

            <View style={styles.formSection}>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Password *</Text>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={styles.input}
                    placeholder="Minimal 8 karakter"
                    placeholderTextColor="#94A3B8"
                    value={password}
                    onChangeText={(value) => setPassword(value)}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Ionicons
                      name={showPassword ? "eye-outline" : "eye-off-outline"}
                      size={20}
                      color="#64748B"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.note}>{`Minimal 8 karakter, wajib mengandung satu angka, satu huruf kapital dan satu karakter khusus`}</Text>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Konfirmasi Password *</Text>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={styles.input}
                    placeholder="Konfirmasi password"
                    placeholderTextColor="#94A3B8"
                    value={confirmPassword}
                    onChangeText={(value) => setConfirmPassword(value)}
                    secureTextEntry={!showConfirmPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <Ionicons
                      name={showConfirmPassword ? "eye-outline" : "eye-off-outline"}
                      size={20}
                      color="#64748B"
                    />
                  </TouchableOpacity>
                </View>
              </View>

              <TouchableOpacity
                style={[styles.registerButton, (isLoading || !password || !confirmPassword) && styles.disabledButton]}
                onPress={handleCreatePassword}
                disabled={(isLoading || !password || !confirmPassword)}
              >
                <Text style={styles.registerButtonText}>
                  {isLoading ? 'Memproses...' : 'Lanjutkan'}
                </Text>
              </TouchableOpacity>

              {bannerWrapper}
            </View>
          </View>
        </ScrollView>
      </View>

      {customAlertModal}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingTop: 32,
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  titleSection: {
    marginBottom: 32,
  },
  titleIconContainer: {
    width: 64,
    height: 64,
    marginHorizontal: 'auto',
    borderRadius: 'calc(infinity * 1px)',
    backgroundColor: '#F8FAFC',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    width: '100%',
    fontSize: 20,
    lineHeight: 28,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#00000',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    width: '100%',
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
  },
  formSection: {
    flex: 1,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    color: '#1E293B',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  inputContainerDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  inputDisabled: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    backgroundColor: '#E2E8F0',
  },
  eyeIcon: {
    padding: 4,
  },
  inputIcon: {
    marginLeft: 8,
  },
  uploadContainer: {
    borderWidth: 1,
    borderColor: '#94A3B8',
    borderStyle: 'dashed',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    height: 136,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadContent: {
    alignItems: 'center',
    width: 208,
  },
  uploadText: {
    fontSize: 14,
    color: '#1E293B',
    marginTop: 8,
    fontWeight: '600',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  uploadSubtext: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 4,
    fontFamily: 'Poppins',
    textAlign: 'center',
    lineHeight: 16,
  },
  uploadContainerProcessing: {
    borderColor: '#3B82F6',
    backgroundColor: '#F0F9FF',
  },
  ocrHintText: {
    fontSize: 11,
    color: '#3B82F6',
    marginTop: 6,
    fontFamily: 'Poppins',
    textAlign: 'center',
    fontWeight: '500',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  checkboxText: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  linkText: {
    color: '#3B82F6',
    fontWeight: '500',
  },
  registerButton: {
    backgroundColor: '#1E293B',
    borderRadius: 6,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabledButton: {
    opacity: 0.5,
  },
  registerButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: 400,
    color: '#64748B',
  },
  loginLink: {
    fontSize: 14,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#1E293B',
  },
  note: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 6
  },
});